<script setup lang="ts">
import { ref } from 'vue';
import Accordion from 'primevue/accordion';
import AccordionPanel from 'primevue/accordionpanel';
import AccordionHeader from 'primevue/accordionheader';
import AccordionContent from 'primevue/accordioncontent';
import Footer from '@/components/Footer.vue';

// Import each section component
import Section1 from '@/components/app/b2b/sections/Section1.vue';

// Track expanded accordion panel
const expandedValue = ref<number>(0); // Default expand first section

// Accordion sections structure
const sections = [
  { title: 'Business Partners', content: Section1 },
];
</script>

<template>
  <div class="legal-page">
    <div class="legal-container animate-on-scroll active">
      <div class="badge">PARTNERS</div>
      <h1>Partner <span class="text-accent">With Us</span></h1>
      
      <div class="glass legal-card">
        <Accordion :value="expandedValue">
          <AccordionPanel
            v-for="(section, index) in sections"
            :key="index"
            :value="index"
          >
            <AccordionHeader>{{ section.title }}</AccordionHeader>
            <AccordionContent>
              <div class="prose">
                <component :is="section.content" />
              </div>
            </AccordionContent>
          </AccordionPanel>
        </Accordion>

<!--        <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-8 pt-6 border-t border-[rgba(255,255,255,0.05)]">-->
<!--          <p class="mb-1 sm:mb-0 date-text"><strong>Effective Date:</strong> 1st October 2025</p>-->
<!--          <p class="date-text"><strong>Last Updated:</strong> 25th May 2026</p>-->
<!--        </div>-->
      </div>
    </div>
    <Footer />
  </div>
</template>

<style scoped>
.legal-page {
  width: 100%;
  background: var(--bg-dark);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.legal-container {
  max-width: 1200px;
  width: 90%;
  margin: 120px auto 60px;
  flex-grow: 1;
}

.badge {
  display: inline-block;
  background: rgba(230, 0, 0, 0.1);
  color: var(--red-primary);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 20px;
  border: 1px solid rgba(230, 0, 0, 0.2);
}

h1 {
  font-size: clamp(2.2rem, 6vw, 3.5rem);
  font-weight: 900;
  margin-bottom: 40px;
  color: #fff;
  line-height: 1.1;
}

.legal-card {
  padding: 30px;
  border-radius: var(--radius-lg);
  border: 1px solid var(--border-color);
  background: var(--bg-card);
  backdrop-filter: var(--glass-blur);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
}

.date-text {
  color: var(--text-secondary);
  font-size: 0.9rem;
  font-family: var(--font-accent);
}

/* Custom Accordion overrides */
:deep(.p-accordion) {
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: transparent;
}

:deep(.p-accordionpanel) {
  border: 1px solid rgba(255, 255, 255, 0.05) !important;
  border-radius: var(--radius-md) !important;
  background: rgba(255, 255, 255, 0.01);
  overflow: hidden;
  transition: all 0.3s ease;
}

:deep(.p-accordionpanel:hover) {
  border-color: rgba(230, 0, 0, 0.2) !important;
  background: rgba(255, 255, 255, 0.02);
}

:deep(.p-accordionheader) {
  background: transparent !important;
  color: var(--text-primary) !important;
  font-family: var(--font-main) !important;
  font-size: 1.15rem !important;
  font-weight: 600 !important;
  padding: 20px 24px !important;
  border: none !important;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  width: 100%;
  text-align: left;
}

:deep(.p-accordionpanel-active) {
  border-color: rgba(230, 0, 0, 0.4) !important;
  background: rgba(230, 0, 0, 0.02) !important;
  box-shadow: 0 0 15px rgba(230, 0, 0, 0.05);
}

:deep(.p-accordionpanel-active .p-accordionheader) {
  color: var(--red-glow) !important;
}

:deep(.p-accordioncontent) {
  background: transparent !important;
  border: none !important;
  padding: 0 24px 24px !important;
}

/* Deep prose styles matching copied Tailwind utility components */
:deep(.prose) {
  color: var(--text-secondary) !important;
  font-family: var(--font-accent) !important;
  font-size: 0.98rem !important;
  line-height: 1.6 !important;
}

:deep(.prose p) {
  margin-bottom: 1rem !important;
}

:deep(.prose p:last-child) {
  margin-bottom: 0 !important;
}

:deep(.prose a) {
  color: var(--red-glow) !important;
  text-decoration: underline !important;
  transition: color 0.2s ease !important;
}

:deep(.prose a:hover) {
  color: #fff !important;
}

:deep(.prose ul) {
  padding-left: 20px !important;
}

:deep(.prose ul li) {
  position: relative !important;
  list-style: none !important;
  margin-bottom: 8px !important;
}

:deep(.prose ul li::before) {
  content: "•" !important;
  position: absolute !important;
  left: -15px !important;
  color: var(--red-primary) !important;
  font-weight: bold !important;
}

@media (max-width: 768px) {
  .legal-container {
    margin-top: 90px;
    margin-bottom: 40px;
  }

  .legal-card {
    padding: 20px;
  }

  :deep(.p-accordionheader) {
    padding: 16px !important;
    font-size: 1.05rem !important;
  }

  :deep(.p-accordioncontent) {
    padding: 0 16px 16px !important;
  }
}
</style>
